import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Crown, Loader2, Shield, Lock, CreditCard, Apple, Smartphone, Gamepad2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { SubscribeBanner } from "@/components/SubscribeBanner";

export default function Subscribe() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { user, paid } = useAuth();
  const { tiers } = useCurrency();
  const [cfg, setCfg] = useState(null);
  const [selected, setSelected] = useState("standard");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/subscription/config").then(({ data }) => setCfg(data)); }, []);

  const unlocked = paid || cfg?.free_access;
  const tier = tiers.find((t) => t.id === selected) || tiers[1];

  // Redirect to the selected plan's Paystack hosted payment link.
  const goToCheckout = () => {
    if (!user) { nav("/login"); return; }
    if (!tier?.link) return;
    setLoading(true);
    window.location.href = tier.link;
  };

  return (
    <div className="pt-24 pb-20 px-4 sm:px-8 lg:px-12 min-h-screen relative overflow-hidden" data-testid="subscribe-page">
      {!unlocked && <div className="absolute top-16 inset-x-0 z-30"><SubscribeBanner /></div>}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-brand/20 rounded-full blur-[120px]" />

      <div className="relative max-w-5xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/20 text-gold text-xs font-bold uppercase tracking-widest mb-4">
          <Crown size={14} /> NeTimeTV
        </div>
        <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight mb-3">
          {cfg?.free_access ? (user ? t("free.subTitle") : t("free.join")) : unlocked ? t("subscribe.allSet") : t("subscribe.trialHeading")}
        </h1>
        {!unlocked && (
          <p className="text-zinc-400 max-w-xl mx-auto mb-10">
            {t("subscribe.intro")}
          </p>
        )}

        {cfg?.free_access ? (
          <div data-testid="free-mode-card" className="max-w-md mx-auto bg-card border border-emerald-500/40 rounded-2xl p-8">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold mb-4">{t("free.badge")}</span>
            <p className="font-head text-2xl font-bold mb-2">{t("free.subTitle")}</p>
            <p className="text-zinc-400 text-sm mb-6">{t("free.subBody")}</p>
            {user ? (
              <button onClick={() => nav("/")} data-testid="free-start-watching-btn"
                className="bg-white text-black font-bold px-6 py-3 rounded-lg hover:bg-brand hover:text-white transition-colors">{t("free.startWatching")}</button>
            ) : (
              <button onClick={() => nav("/register")} data-testid="free-register-btn"
                className="bg-emerald-500 text-black font-bold px-6 py-3 rounded-lg hover:bg-emerald-400 transition-colors">{t("free.cta")}</button>
            )}
          </div>
        ) : unlocked ? (
          <div className="max-w-md mx-auto bg-card border border-gold/40 rounded-2xl p-8">
            <Crown className="text-gold mx-auto mb-3" size={40} />
            <p className="font-head text-xl font-bold mb-2">{t("subscribe.active")}</p>
            <button onClick={() => nav("/")} data-testid="go-home-btn"
              className="bg-white text-black font-bold px-6 py-3 rounded-lg hover:bg-brand hover:text-white transition-colors">{t("subscribe.startWatching")}</button>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              {tiers.map((t2) => (
                <button key={t2.id} data-testid={`tier-${t2.id}`} onClick={() => setSelected(t2.id)}
                  className={`text-left rounded-2xl p-6 border-2 transition-all ${selected === t2.id ? "border-brand bg-brand/10 scale-[1.02]" : "border-white/10 bg-card hover:border-white/25"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-head text-lg font-bold">{t2.label}</span>
                    {t2.id === "premium" && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gold text-black">{t("subscribe.bestValue")}</span>}
                  </div>
                  <div className="mb-3"><span className="font-head text-4xl font-extrabold">{t2.display}</span><span className="text-zinc-400 text-sm">/mo</span></div>
                  <ul className="space-y-2 text-sm text-zinc-300">
                    <li className="flex items-center gap-2"><Smartphone size={14} className="text-brand" /> {t2.devices === "Phone & Computer" ? t("subscribe.phoneComputer") : t("subscribe.allDevices")}</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-emerald-400" /> {t("subscribe.channelsRadio")}</li>
                    <li className="flex items-center gap-2">{t2.games ? <Check size={14} className="text-emerald-400" /> : <Gamepad2 size={14} className="text-zinc-600" />} <span className={t2.games ? "" : "text-zinc-500 line-through"}>{t("subscribe.gamesSection")}</span></li>
                  </ul>
                </button>
              ))}
            </div>

            <div className="max-w-md mx-auto">
              <button data-testid="subscribe-btn" onClick={goToCheckout} disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover font-bold py-4 rounded-xl transition-colors disabled:opacity-60 text-lg">
                {loading ? <Loader2 className="animate-spin" size={20} /> : <Lock size={18} />}
                {t("subscribe.startTrial")} · {tier?.display}{t("subscribe.afterMonth")}
              </button>
              <div className="mt-4 flex items-center justify-center gap-3 text-zinc-500">
                <span className="flex items-center gap-1 text-[11px]"><Shield size={12} className="text-emerald-400" /> {t("subscribe.secureCheckout")}</span>
                <span className="flex items-center gap-1 text-[11px]"><Apple size={12} /> Apple Pay</span>
                <span className="flex items-center gap-1 text-[11px]"><CreditCard size={12} /> Visa · Mastercard</span>
              </div>
              <p className="text-[11px] text-zinc-600 mt-2">{t("subscribe.poweredBy", { price: tier?.display })}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
